export function emptyCarParts() {
  return { enabled: false, universal: false, applications: [] };
}

export function newFitmentRow() {
  const id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `fit_${Math.random().toString(36).slice(2, 10)}`;
  return { id, make: "", model: "", year_from: "", year_to: "", trim: "" };
}

export function formatFitment(app) {
  if (!app) return "";
  const yf = app.year_from;
  const yt = app.year_to;
  const years = yf && yt ? (yf === yt ? String(yf) : `${yf}–${yt}`) : "";
  const parts = [app.make, app.model, years, app.trim].filter(Boolean);
  return parts.join(" ");
}

export function fitmentSummary(cfg, limit = 2) {
  if (!cfg?.enabled) return "";
  if (cfg.universal) return "Universal fit";
  const apps = cfg.applications || [];
  if (!apps.length) return "";
  const shown = apps.slice(0, limit).map(formatFitment).filter(Boolean);
  const extra = apps.length - shown.length;
  return extra > 0 ? `${shown.join(" · ")} +${extra} more` : shown.join(" · ");
}

export function garageKey(slug) {
  return `ms_garage_${slug || "store"}`;
}

export function loadGarage(slug) {
  try {
    const raw = localStorage.getItem(garageKey(slug));
    const d = raw ? JSON.parse(raw) : {};
    return { year: d.year || "", make: d.make || "", model: d.model || "", trim: d.trim || "" };
  } catch {
    return { year: "", make: "", model: "", trim: "" };
  }
}

export function saveGarage(slug, veh) {
  try {
    localStorage.setItem(garageKey(slug), JSON.stringify({
      year: veh.year || "",
      make: veh.make || "",
      model: veh.model || "",
      trim: veh.trim || "",
    }));
  } catch { /* ignore */ }
}

export function garageActive(veh) {
  return Boolean(veh?.year || veh?.make || veh?.model || veh?.trim);
}

export function modelsForMake(tree, make) {
  if (!make || !tree?.[make]) return [];
  return Object.keys(tree[make]);
}

export function yearsFor(tree, make, model) {
  if (!make || !model) return [];
  return tree?.[make]?.[model]?.years || [];
}

export function trimsFor(tree, make, model, year) {
  if (!make || !model || !year) return [];
  return tree?.[make]?.[model]?.trims?.[String(year)] || [];
}

export function allYears(tree) {
  const set = new Set();
  Object.values(tree || {}).forEach((models) => {
    Object.values(models || {}).forEach((info) => {
      (info.years || []).forEach((y) => set.add(Number(y)));
    });
  });
  return Array.from(set).sort((a, b) => b - a);
}

export function makesForYear(tree, year) {
  const keys = Object.keys(tree || {});
  if (!year) return keys;
  const y = Number(year);
  return keys.filter((make) => Object.values(tree[make] || {}).some((info) => (info.years || []).some((n) => Number(n) === y)));
}

export function uniqueMakes(tree, apps = []) {
  const set = new Set(Object.keys(tree || {}));
  (apps || []).forEach((a) => { if (a?.make) set.add(String(a.make)); });
  return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b));
}

export function uniqueModels(tree, make, apps = []) {
  const set = new Set();
  const addModels = (models) => Object.keys(models || {}).forEach((m) => set.add(m));
  if (make && tree?.[make]) addModels(tree[make]);
  else Object.values(tree || {}).forEach(addModels);
  const want = String(make || "").toLowerCase();
  (apps || []).forEach((a) => {
    if (!a?.model) return;
    if (!want || String(a.make || "").toLowerCase() === want) set.add(String(a.model));
  });
  return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b));
}

export function uniqueYears(tree, make, model, apps = []) {
  const set = new Set();
  const addYears = (info) => (info?.years || []).forEach((y) => set.add(Number(y)));
  if (make && model && tree?.[make]?.[model]) addYears(tree[make][model]);
  else if (make && tree?.[make]) Object.values(tree[make]).forEach(addYears);
  else Object.values(tree || {}).forEach((models) => Object.values(models || {}).forEach(addYears));
  (apps || []).forEach((a) => {
    if (a?.year_from) set.add(Number(a.year_from));
    if (a?.year_to) set.add(Number(a.year_to));
  });
  return Array.from(set).filter((n) => Number.isFinite(n)).sort((a, b) => b - a);
}

export function uniqueTrims(tree, make, model, year, apps = []) {
  const set = new Set();
  const addTrims = (info) => {
    const map = info?.trims || {};
    if (year && map[String(year)]) (map[String(year)] || []).forEach((t) => set.add(t));
    else Object.values(map).forEach((arr) => (arr || []).forEach((t) => set.add(t)));
  };
  if (make && model && tree?.[make]?.[model]) addTrims(tree[make][model]);
  else if (make && tree?.[make]) Object.values(tree[make]).forEach(addTrims);
  else Object.values(tree || {}).forEach((models) => Object.values(models || {}).forEach(addTrims));
  (apps || []).forEach((a) => { if (a?.trim) set.add(String(a.trim)); });
  return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b));
}

export function modelsForYearMake(tree, year, make) {
  if (!make || !tree?.[make]) return [];
  const models = Object.keys(tree[make]);
  if (!year) return models;
  const y = Number(year);
  return models.filter((model) => (tree[make][model].years || []).some((n) => Number(n) === y));
}
